import { html, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { DodonaElement } from "@/components/meta/dodona_element";
import { fetch, NetworkError, ResponseError } from "@/utilities";
import { Toast } from "@/toast";
import { I18n } from "@/i18n/i18n";
import type { ActivityStatusChange } from "@/components/activity_series";

// What ActivityReadStatesController answers when it saved a read.
type SavedRead = { read_at: string; read_date: string; series_id: number | null };

// The server's message for a refused read, which also covers an assessment this browser isn't connected to.
async function failureMessage(error: unknown): Promise<string> {
    if (error instanceof NetworkError) return I18n.t("js.read-network");
    if (error instanceof ResponseError) {
        const answer = await error.response.json().catch(() => null);
        if (typeof answer?.message === "string") return answer.message;
    }
    return I18n.t("js.read-failed");
}

/**
 * The Mark as read card of a reading activity. Activities::ReadCardComponent renders the element
 * with the activity's read state, and the element draws the card from it.
 *
 * The button saves the read with fetch. The server answers with the time of the read, as ISO 8601
 * and formatted for display, and the card shows its read state; the read date is announced in the
 * status region next to the card. The series views and the right rail learn of the read from an
 * `activity-status-changed` event, for the series the read was saved in.
 * While the request runs, the button says to wait and ignores clicks, but keeps focus. When the
 * request fails, the button comes back and a toast shows the server's message.
 * A user who can't mark the activity as read gets a card headed with the reading status, without
 * the button; the server renders the reason inside the element, and the card draws its header
 * before it.
 *
 * Styles: app/assets/stylesheets/components/card.css.scss (.card-eyebrow)
 *
 * @element d-read-card
 *
 * @prop {string} action - URL the read is posted to.
 * @prop {number} activityId - The activity the card marks as read.
 * @prop {number|null} seriesId - The series the read is saved in, if any.
 * @prop {number|null} courseId - The course the read is saved in, if any.
 * @prop {string} icon - Icon of the activity, without the `mdi-` prefix.
 * @prop {boolean} canRead - Whether the user may mark the activity as read now.
 * @prop {string|null} readAt - When the activity was read, as ISO 8601, or null while it is unread.
 * @prop {string|null} readDate - That time as the server formats it for display.
 */
@customElement("d-read-card")
export class ReadCard extends DodonaElement {
    @property({ type: String })
    action = "";

    @property({ type: Number, attribute: "activity-id" })
    activityId: number;

    @property({ type: Number, attribute: "series-id" })
    seriesId: number | null = null;

    @property({ type: Number, attribute: "course-id" })
    courseId: number | null = null;

    @property({ type: String })
    icon = "";

    @property({ type: Boolean, attribute: "can-read" })
    canRead = false;

    @property({ type: String, attribute: "read-at", reflect: true })
    readAt: string | null = null;

    @property({ type: String, attribute: "read-date" })
    readDate: string | null = null;

    @state()
    private saving = false;

    createRenderRoot(): HTMLElement {
        this.renderOptions.renderBefore ??= this.firstChild;
        return super.createRenderRoot();
    }

    private get read(): boolean {
        return this.readAt !== null;
    }

    private get cardTitle(): string {
        if (this.read) return I18n.t("js.read_card.read_title");
        return this.canRead ? I18n.t("js.read_card.title") : I18n.t("js.activity_status.not_read");
    }

    // A blocked card has no subtitle: the reason below the title says what is going on.
    private get cardSubtitle(): string | null {
        if (this.read) return I18n.t("js.read_card.read_at", { timestamp: this.readDate });
        return this.canRead ? I18n.t("js.read_card.subtitle") : null;
    }

    private async markAsRead(): Promise<void> {
        if (this.saving) return;

        this.saving = true;
        const body = new FormData();
        body.append("activity_read_state[activity_id]", String(this.activityId));
        if (this.seriesId !== null) body.append("activity_read_state[series_id]", String(this.seriesId));
        if (this.courseId !== null) body.append("activity_read_state[course_id]", String(this.courseId));

        let saved: SavedRead;
        try {
            const response = await fetch(this.action, {
                method: "POST",
                body,
                headers: { accept: "application/json" },
            });
            saved = await response.json();
        } catch (error) {
            this.saving = false;
            new Toast(await failureMessage(error));
            return;
        }

        // The button goes once the card shows the read state, so focus moves to the card itself.
        const hadFocus = this.contains(document.activeElement);
        this.saving = false;
        this.readAt = saved.read_at;
        this.readDate = saved.read_date;
        await this.updateComplete;
        if (hadFocus) {
            this.focus();
        }
        document.querySelector("#read-card-status").textContent = this.cardSubtitle;

        const change: ActivityStatusChange = {
            activityId: this.activityId,
            seriesId: saved.series_id,
            state: "accepted",
            at: saved.read_at,
            accepted: true,
            readDate: saved.read_date,
        };
        document.dispatchEvent(new CustomEvent("activity-status-changed", { detail: change }));
    }

    render(): TemplateResult {
        return html`
            <div class="card-eyebrow card-eyebrow--wrap">
                <span class="card-eyebrow-icon" aria-hidden="true"><i class="mdi mdi-24 mdi-${this.icon}"></i></span>
                <div class="card-eyebrow-text">
                    <h2 class="card-eyebrow-title">${this.cardTitle}</h2>
                    ${this.cardSubtitle ? html`<span class="card-eyebrow-subtitle">${this.cardSubtitle}</span>` : ""}
                </div>
                ${
                    this.read || !this.canRead
                        ? ""
                        : html`
                              <div class="card-eyebrow-actions">
                                  <button
                                      type="button"
                                      class="btn btn-filled with-icon ${this.saving ? "disabled" : ""}"
                                      aria-disabled=${this.saving ? "true" : "false"}
                                      @click=${this.markAsRead}
                                  >
                                      <i class="mdi mdi-clipboard-check-outline icon" aria-hidden="true"></i>
                                      <span class="text">
                                          ${this.saving ? I18n.t("js.read_card.saving") : I18n.t("js.read_card.mark_as_read")}
                                      </span>
                                  </button>
                              </div>
                          `
                }
            </div>
        `;
    }
}
